import { observer } from 'mobx-react';
import React from 'react';
import { Button, Grid, GridColumn, Heading, Icon, List, ListItem, Modal, ModalCloser, ModalContent, ModalFooter, Text } from '@teliaee/design-system';
import useStores from '../stores/UseStores';
import { useIsTabletHook } from '../util/hooks/useIsMobile';

const PasskeyOfferRegister = observer((): React.JSX.Element => {
  const {
    passkeyStore: { offerRegisterModalVisible, offerRegisterModalOnComplete, register, skipPasskeyOfferRegister, wasPasskeyRegisterSuccessful },
    translateStore: { translate },
  } = useStores();
  const { isTablet } = useIsTabletHook();

  const skipPasskeyRegister = async () => {
    await skipPasskeyOfferRegister();
    offerRegisterModalOnComplete();
  };

  const addPasskey = async () => {
    await register();
  };

  const handleRegisterResultClose = () => {
    offerRegisterModalOnComplete();
  };

  if (wasPasskeyRegisterSuccessful !== null) {
    const labelPrefix = `passkey.offerRegister.result.${wasPasskeyRegisterSuccessful ? 'success' : 'error'}`;

    return (
      <Modal isOpen={offerRegisterModalVisible} isFullHeight={!isTablet} onClose={handleRegisterResultClose}>
        <ModalContent>
          <Text className="text-center">
            <Icon
              size="lg"
              modifiers={`#${wasPasskeyRegisterSuccessful ? 'check-round' : 'alert'}`}
              className={`c-${wasPasskeyRegisterSuccessful ? 'success' : 'error'}-text`}
            />
            <Heading level={1} className="h3">
              {translate(`${labelPrefix}.title`)}
            </Heading>
            <p>{translate(`${labelPrefix}.text`)}</p>
            {!wasPasskeyRegisterSuccessful ? (
              <Button variant="secondary" text={translate(`${labelPrefix}.retry`)} iconLeft="#refresh" onClick={addPasskey} />
            ) : (
              <ModalCloser>
                <Button variant="secondary" text={translate('passkey.offerRegister.result.close')} onClick={handleRegisterResultClose} />
              </ModalCloser>
            )}
          </Text>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={offerRegisterModalVisible} isFullHeight={!isTablet} onClose={skipPasskeyRegister}>
      <ModalContent>
        <Text>
          <Grid>
            <GridColumn width={['xs-11']}>
              <Heading level={1} className="h3">
                {translate('passkey.offerRegister.heading')}
              </Heading>
            </GridColumn>
          </Grid>
          <p>{translate('passkey.offerRegister.subHeading')}</p>
          <List type="icon">
            <ListItem icon="#fingerprint">{translate('passkey.offerRegister.listItem.fingerprint')}</ListItem>
            <ListItem icon="#face-id">{translate('passkey.offerRegister.listItem.faceId')}</ListItem>
            <ListItem icon="#phone-lock">{translate('passkey.offerRegister.listItem.phoneLock')}</ListItem>
          </List>
          <Button link={true} text={translate('panel.passkey.cta')} target="_blank" url={translate('panel.passkey.cta.url')} />
        </Text>
      </ModalContent>
      <ModalFooter>
        <Grid align={isTablet ? ['between-xs'] : ['around-xs']}>
          <ModalCloser>
            <Button text={translate('passkey.offerRegister.footer.skip')} link onClick={skipPasskeyRegister} />
          </ModalCloser>
          <ModalCloser>
            <Button text={translate('passkey.offerRegister.footer.addPasskey')} type="expressive" onClick={addPasskey} />
          </ModalCloser>
        </Grid>
      </ModalFooter>
    </Modal>
  );
});

export default PasskeyOfferRegister;
